import { useState, useEffect } from 'react';
import { useNavigate, Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import Logo from '../components/Logo';

function postAuthRedirectPath(searchParams) {
  const raw = searchParams.get('next') || searchParams.get('returnTo');
  if (raw && raw.startsWith('/') && !raw.startsWith('//')) return raw;
  return '/app/dashboard';
}

function readInviteToken(searchParams) {
  const raw = searchParams.get('invite') || searchParams.get('token');
  return raw && String(raw).trim() ? String(raw).trim() : null;
}

export default function SignupPage() {
  const { signup, user, sessionReady } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = readInviteToken(searchParams);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(!!inviteToken);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteExpiresAt, setInviteExpiresAt] = useState(null);

  useEffect(() => {
    if (!inviteToken) {
      setInviteLoading(false);
      setInviteValid(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setInviteLoading(true);
      setError('');
      try {
        const data = await authAPI.validateInvite(inviteToken);
        if (cancelled) return;
        if (data?.valid) {
          setInviteValid(true);
          setInviteExpiresAt(data.expiresAt || null);
        } else {
          setInviteValid(false);
          setError(data?.error || 'This invitation link is invalid or has expired.');
        }
      } catch (err) {
        if (!cancelled) {
          setInviteValid(false);
          setError(err.message || 'This invitation link is invalid or has expired.');
        }
      } finally {
        if (!cancelled) setInviteLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  if (!sessionReady) {
    return (
      <div className="app-auth-page app-dashboard-backdrop">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading…</p>
      </div>
    );
  }

  if (user) {
    return <Navigate to={postAuthRedirectPath(searchParams)} replace />;
  }

  if (!inviteToken) {
    return (
      <div className="app-auth-page app-dashboard-backdrop px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center justify-center" aria-label="Stavyn">
              <Logo style={{ height: 36, width: 'auto' }} />
            </Link>
          </div>
          <div className="app-auth-card text-center">
            <h1 className="app-auth-card__title">Invitation required</h1>
            <p className="app-auth-card__subtitle">
              Accounts are invite-only. Ask your organization administrator for a signup link, then return here to
              complete registration.
            </p>
            <Link
              to={`/login${searchParams.toString() ? `?${searchParams.toString()}` : ''}`}
              className="btn-primary inline-flex justify-center"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    if (!inviteValid) {
      setError('A valid invitation is required to create an account.');
      return;
    }
    const nameClean = String(name || '').trim();
    const emailClean = String(email || '').trim().toLowerCase();
    if (!nameClean || !emailClean) {
      setError('Name and email are required.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      await signup(nameClean, emailClean, password, inviteToken);
      navigate(postAuthRedirectPath(searchParams), { replace: true });
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-auth-page app-dashboard-backdrop px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center" aria-label="Stavyn">
            <Logo style={{ height: 36, width: 'auto' }} />
          </Link>
        </div>

        <div className="app-auth-card">
          <h1 className="app-auth-card__title">Accept your invitation</h1>
          <p className="app-auth-card__subtitle">Create your account to join your organization on Stavyn</p>

          {inviteLoading && (
            <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>Verifying invitation…</p>
          )}

          {error && (
            <div className="app-auth-error">{error}</div>
          )}

          {!inviteLoading && inviteValid && inviteExpiresAt && (
            <p className="text-xs mb-4" style={{ color: 'var(--text-subtle)' }}>
              Invitation expires {new Date(inviteExpiresAt).toLocaleString()}
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="app-auth-label" htmlFor="signup-name">Full name</label>
              <input
                id="signup-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!inviteValid || loading}
                className="app-input disabled:opacity-60"
                placeholder="Jane Smith"
              />
            </div>
            <div>
              <label className="app-auth-label" htmlFor="signup-email">Work email</label>
              <input
                id="signup-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!inviteValid || loading}
                className="app-input disabled:opacity-60"
                placeholder="you@company.com"
                autoComplete="email"
              />
              {inviteValid && (
                <p className="mt-1 text-xs" style={{ color: 'var(--text-subtle)' }}>
                  Enter the same email address your invitation was sent to.
                </p>
              )}
            </div>
            <div>
              <label className="app-auth-label" htmlFor="signup-password">Password</label>
              <input
                id="signup-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={!inviteValid || loading}
                className="app-input disabled:opacity-60"
                placeholder="Min. 6 characters"
              />
            </div>
            <button
              type="submit"
              disabled={loading || inviteLoading || !inviteValid}
              className="btn-primary w-full justify-center disabled:opacity-60"
              style={{ display: 'flex' }}
            >
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </form>

          <p className="text-center text-xs mt-4" style={{ color: 'var(--text-subtle)' }}>
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="app-auth-link">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="app-auth-link">
              Privacy Policy
            </Link>
            .
          </p>

          <p className="text-center text-sm mt-4" style={{ color: 'var(--text-muted)' }}>
            Already have an account?{' '}
            <Link
              to={`/login${searchParams.toString() ? `?${searchParams.toString()}` : ''}`}
              className="app-auth-link"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
